/* ==========================================================================
   RETRO DOG SHOP  ·  Theme-Skript
   Kein Framework, keine externen Abhaengigkeiten. Alles ueber Custom Elements
   und die Shopify Ajax-API (/cart/add.js, /cart/change.js, /search/suggest).
   ========================================================================== */

(function () {
  'use strict';

  const money = (cents) => {
    const format = (window.RDS && window.RDS.moneyFormat) || '{{amount_with_comma_separator}} EUR';
    const value = (cents / 100).toFixed(2);
    if (format.indexOf('comma_separator') !== -1) {
      const parts = value.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return format.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, int + ',' + parts[1]);
    }
    return format.replace(/\{\{\s*amount\s*\}\}/, value);
  };

  const t = (key, fallback) =>
    (window.RDS && window.RDS.strings && window.RDS.strings[key]) || fallback;

  /* ----------------------------------------------------------------------
     Drawer (Menue + Warenkorb)
     ---------------------------------------------------------------------- */

  class RdsDrawer extends HTMLElement {
    connectedCallback() {
      this.scrim = this.querySelector('[data-scrim]');
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      document.body.classList.add('is-locked');
      this.setAttribute('aria-hidden', 'false');
      const focusable = this.querySelector('button, a[href], input, select');
      window.requestAnimationFrame(() => focusable && focusable.focus());
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      this.setAttribute('aria-hidden', 'true');
    }
  }
  customElements.define('rds-drawer', RdsDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.open();
  });

  /* ----------------------------------------------------------------------
     Warenkorb
     ---------------------------------------------------------------------- */

  const Cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.description || data.message || t('cartError', 'Fehler im Warenkorb'));
      }
      return data;
    },

    add(items) {
      return this.request('/cart/add.js', { items });
    },

    change(payload) {
      return this.request('/cart/change.js', payload);
    },

    async state() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },

    async refresh() {
      const cart = await this.state();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.dispatchEvent(new CustomEvent('rds:cart:updated', { detail: cart }));
      return cart;
    }
  };

  window.RDSCart = Cart;

  /* ----------------------------------------------------------------------
     In den Warenkorb legen
     ---------------------------------------------------------------------- */

  class RdsProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.errorBox = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(event) {
      if (!this.button || this.button.hasAttribute('aria-disabled')) return;
      event.preventDefault();

      const idField = this.form.querySelector('[name="id"]');
      const qtyField = this.form.querySelector('[name="quantity"]');
      const id = idField && idField.value;
      const qty = parseInt((qtyField && qtyField.value) || '1', 10);
      if (!id) return;

      const original = this.button.textContent;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.textContent = t('adding', 'Wird hinzugefuegt');
      if (this.errorBox) this.errorBox.hidden = true;

      try {
        await Cart.add([{ id: Number(id), quantity: qty }]);
        await Cart.refresh();
        const drawer = document.getElementById('cart-drawer');
        if (drawer) drawer.open();
      } catch (err) {
        if (this.errorBox) {
          this.errorBox.textContent = err.message;
          this.errorBox.hidden = false;
        }
      } finally {
        this.button.removeAttribute('aria-disabled');
        this.button.textContent = original;
      }
    }
  }
  customElements.define('rds-product-form', RdsProductForm);

  /* ----------------------------------------------------------------------
     Warenkorb-Zeilen (Drawer und Warenkorbseite)
     ---------------------------------------------------------------------- */

  class RdsCartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
    }

    async onClick(event) {
      const remove = event.target.closest('[data-line-remove]');
      if (remove) {
        event.preventDefault();
        await this.update(remove.dataset.lineRemove, 0);
        return;
      }
      const step = event.target.closest('[data-qty-step]');
      if (!step) return;
      const input = step.parentElement.querySelector('input');
      const next = Math.max(0, parseInt(input.value, 10) + Number(step.dataset.qtyStep));
      input.value = next;
      await this.update(step.closest('[data-line]').dataset.line, next);
    }

    async onChange(event) {
      const input = event.target.closest('[data-line-qty]');
      if (!input) return;
      await this.update(input.closest('[data-line]').dataset.line, parseInt(input.value, 10));
    }

    async update(line, quantity) {
      this.classList.add('is-loading');
      try {
        await Cart.change({ line: Number(line), quantity: quantity });
        await Cart.refresh();
        await this.rerender();
      } catch (e) {
        window.location.reload();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async rerender() {
      if (!this.sectionId) return;
      const res = await fetch(window.location.pathname + '?section_id=' + this.sectionId);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('rds-cart-items[data-section-id="' + this.sectionId + '"]');
      if (fresh) this.innerHTML = fresh.innerHTML;
      const totals = document.querySelector('[data-cart-totals]');
      const freshTotals = doc.querySelector('[data-cart-totals]');
      if (totals && freshTotals) totals.innerHTML = freshTotals.innerHTML;
    }
  }
  customElements.define('rds-cart-items', RdsCartItems);

  /* ----------------------------------------------------------------------
     Warenkorb-Drawer: Inhalt per Section-Rendering nachladen
     ---------------------------------------------------------------------- */

  document.addEventListener('rds:cart:updated', async () => {
    const host = document.querySelector('[data-cart-drawer-content]');
    if (!host) return;
    const res = await fetch('/?section_id=cart-drawer');
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-content]');
    if (fresh) host.innerHTML = fresh.innerHTML;
  });

  /* ----------------------------------------------------------------------
     Varianten-Auswahl
     ---------------------------------------------------------------------- */

  class RdsVariants extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = JSON.parse((data && data.textContent) || '[]');
      this.idInput = document.querySelector('[data-variant-id]');
      this.addEventListener('change', () => this.onChange());
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input:checked')).map((i) => i.value);
    }

    onChange() {
      const chosen = this.selectedOptions;
      const match = this.variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
      if (!match) return this.setUnavailable();

      if (this.idInput) this.idInput.value = match.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());

      this.updatePrice(match);
      this.updateButton(match);
      this.updateMedia(match);
    }

    updatePrice(variant) {
      const priceEl = document.querySelector('[data-price-current]');
      const compareEl = document.querySelector('[data-price-compare]');
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.textContent = onSale ? money(variant.compare_at_price) : '';
        compareEl.hidden = !onSale;
      }
    }

    updateButton(variant) {
      const button = document.querySelector('[data-add-button]');
      const stock = document.querySelector('[data-stock]');
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available
          ? t('addToCart', 'In den Warenkorb')
          : t('soldOut', 'Nicht auf Lager');
      }
      if (stock) {
        stock.className = 'stock ' + (variant.available ? 'stock--in' : 'stock--out');
        stock.textContent = variant.available
          ? t('inStock', 'Auf Lager')
          : t('soldOut', 'Nicht auf Lager');
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const thumb = document.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (thumb) thumb.click();
    }

    setUnavailable() {
      const button = document.querySelector('[data-add-button]');
      if (button) {
        button.disabled = true;
        button.textContent = t('unavailable', 'Nicht verfuegbar');
      }
    }
  }
  customElements.define('rds-variants', RdsVariants);

  /* ----------------------------------------------------------------------
     Galerie
     ---------------------------------------------------------------------- */

  class RdsGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main] img');
      this.querySelectorAll('[data-media-id]').forEach((thumb) => {
        thumb.addEventListener('click', () => this.select(thumb));
      });
    }

    select(thumb) {
      const img = thumb.querySelector('img');
      if (!img || !this.main) return;
      this.main.src = img.dataset.full || img.src;
      this.main.srcset = img.dataset.fullSrcset || '';
      this.main.alt = img.alt;
      this.querySelectorAll('[data-media-id]').forEach((el) =>
        el.setAttribute('aria-current', el === thumb ? 'true' : 'false')
      );
    }
  }
  customElements.define('rds-gallery', RdsGallery);

  /* ----------------------------------------------------------------------
     Mengen-Stepper ausserhalb des Warenkorbs
     ---------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step || step.closest('rds-cart-items')) return;
    const input = step.parentElement.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, parseInt(input.value, 10) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------------------
     Sofortsuche
     ---------------------------------------------------------------------- */

  class RdsSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive]');
      this.timer = null;
      if (this.input) {
        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.hide();
        });
      }
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hide();
      });
    }

    onInput() {
      window.clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < 2) return this.hide();
      this.timer = window.setTimeout(() => this.fetch(q), 260);
    }

    async fetch(query) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection',
        'resources[limit]': '6',
        section_id: 'predictive-search'
      });
      try {
        const res = await fetch('/search/suggest?' + params.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-predictive-inner]');
        if (fresh && this.results) {
          this.results.innerHTML = fresh.outerHTML;
          this.results.hidden = false;
        }
      } catch (e) {
        this.hide();
      }
    }

    hide() {
      if (this.results) this.results.hidden = true;
    }
  }
  customElements.define('rds-search', RdsSearch);

  /* ----------------------------------------------------------------------
     Filter und Sortierung (Kollektion)
     ---------------------------------------------------------------------- */

  class RdsFacets extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="checkbox"], select')) this.submit();
      });
    }

    submit() {
      const form = this.querySelector('form');
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      this.render(window.location.pathname + '?' + params.toString());
    }

    async render(url) {
      const grid = document.querySelector('[data-collection-results]');
      if (grid) grid.classList.add('is-loading');
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-collection-results]');
      const newFacets = doc.querySelector('rds-facets');
      if (newGrid && grid) grid.innerHTML = newGrid.innerHTML;
      if (newFacets) this.innerHTML = newFacets.innerHTML;

      window.history.pushState({}, '', url);
      if (grid) {
        grid.classList.remove('is-loading');
        grid.scrollIntoView({ block: 'start' });
      }
    }
  }
  customElements.define('rds-facets', RdsFacets);

  /* ----------------------------------------------------------------------
     Mega-Menue schliesst beim Klick nach aussen
     ---------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.platform-nav__item[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
})();
